import type { Clef } from '../clef.ts';
import { pitchAtPosition } from '../clef.ts';
import { pitchId } from '../pitch.ts';
import { validateNoteIdConfig, type NoteIdConfig } from '../exercises/note-identification/config.ts';
import { generateNoteIdQuestions, type NoteIdQuestion } from '../exercises/note-identification/generate.ts';
import { requiredLessonsBefore } from '../curriculum/resolve.ts';

/**
 * Note-reading placement: three short stages, each checking a block of the
 * path. Placement only ever lands on implemented lessons; skipped lessons are
 * recorded as "placed" (not "completed") and stay open for review.
 *
 *   Stage A — staff notes (units 1–3)      → fail: start at the beginning
 *   Stage B — ledger lines (units 4–5)     → fail: start at unit 4
 *   Stage C — accidentals (unit 7)         → fail: start at unit 7
 *   all passed                              → start at the cumulative review (unit 8)
 */
export interface PlacementStage {
  id: 'staff' | 'ledger' | 'accidentals';
  title: string;
  positions: number[];
  accidentals: NoteIdConfig['accidentals'];
  answerMode: NoteIdConfig['answerMode'];
  questionCount: number;
  passCorrect: number;
  /** Lesson to start at if this stage is not passed. */
  failStartsAt: string;
}

export const PLACEMENT_STAGES: readonly PlacementStage[] = [
  {
    id: 'staff',
    title: 'Notes on the staff',
    positions: [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    accidentals: ['natural'],
    answerMode: 'letter',
    questionCount: 5,
    passCorrect: 4,
    failStartsAt: 'nr-u1-meet-the-staff',
  },
  {
    id: 'ledger',
    title: 'Ledger lines',
    positions: [-7, -6, -5, -4, -3, -2, 10, 11, 12, 13, 14, 15],
    accidentals: ['natural'],
    answerMode: 'letter',
    questionCount: 5,
    passCorrect: 4,
    failStartsAt: 'nr-u4-first-below',
  },
  {
    id: 'accidentals',
    title: 'Sharps and flats',
    positions: [0, 1, 2, 3, 4, 5, 6, 7, 8],
    accidentals: ['natural', 'sharp', 'flat'],
    answerMode: 'spelled',
    questionCount: 5,
    passCorrect: 4,
    failStartsAt: 'nr-u7-sharps',
  },
];

export const PLACEMENT_ALL_PASSED_STARTS_AT = 'nr-u8-mixed-review';
export const PLACEMENT_VERSION = 1;

export function placementStageConfig(stage: PlacementStage, clef: Clef): NoteIdConfig {
  const pool = stage.positions.map((pos) => pitchId(pitchAtPosition(clef, pos)));
  const low = pitchAtPosition(clef, Math.min(...stage.positions));
  const high = pitchAtPosition(clef, Math.max(...stage.positions));
  const result = validateNoteIdConfig({
    clef,
    range: { low: pitchId(low), high: pitchId(high) },
    accidentals: stage.accidentals,
    answerMode: stage.answerMode,
    questionCount: stage.questionCount,
    staffFilter: 'all',
    pool,
    accidentalRate: 0.7,
    includeRareSpellings: false,
    hintsAllowed: false,
  });
  if (!result.ok) throw new Error(`Placement stage ${stage.id} invalid in ${clef}`);
  return result.value;
}

export interface PlacementQuestion extends NoteIdQuestion {
  stageId: PlacementStage['id'];
}

/** All placement questions, stage by stage, positions numbered continuously. */
export function generatePlacementQuestions(clef: Clef, seed: string): PlacementQuestion[] {
  const out: PlacementQuestion[] = [];
  for (const stage of PLACEMENT_STAGES) {
    const questions = generateNoteIdQuestions(placementStageConfig(stage, clef), `${seed}:${stage.id}`);
    for (const q of questions) out.push({ ...q, position: out.length, stageId: stage.id });
  }
  return out;
}

export interface PlacementOutcome {
  startLessonId: string;
  /** Required lessons before the start point, recorded as placed. */
  placedLessonIds: string[];
  stageResults: { stageId: PlacementStage['id']; correct: number; total: number; passed: boolean }[];
}

/** `answers`: first-attempt correctness per placement question. Hints are not offered in placement. */
export function evaluatePlacement(answers: readonly { stageId: PlacementStage['id']; correct: boolean }[]): PlacementOutcome {
  const stageResults = PLACEMENT_STAGES.map((stage) => {
    const forStage = answers.filter((a) => a.stageId === stage.id);
    const correct = forStage.filter((a) => a.correct).length;
    return { stageId: stage.id, correct, total: forStage.length, passed: correct >= stage.passCorrect };
  });
  let startLessonId = PLACEMENT_ALL_PASSED_STARTS_AT;
  for (let i = 0; i < PLACEMENT_STAGES.length; i++) {
    if (!stageResults[i]?.passed) {
      startLessonId = (PLACEMENT_STAGES[i] as PlacementStage).failStartsAt;
      break;
    }
  }
  return { startLessonId, placedLessonIds: requiredLessonsBefore(startLessonId), stageResults };
}
